import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertStatus } from "./AlertStatus";
import { StarChart } from "./StarChart";
import { CrewPanel } from "./CrewPanel";
import { ShipStatus } from "./ShipStatus";
import { OrdersPanel } from "./OrdersPanel";
import { CommPanel } from "./CommPanel";
import { CommandInput } from "./CommandInput";
import { TacticalMap } from "./TacticalMap";
import { CaptainsLog } from "./CaptainsLog";
import { TargetLock } from "./TargetLock";
import { MissionDebrief } from "./MissionDebrief";
import { MissionPanel } from "./MissionPanel";
import { FloatingPanel } from "./FloatingPanel";
import { BridgeLayout } from "./bridge/BridgeLayout";
import { ViewscreenContainer } from "./bridge/ViewscreenContainer";
import { ViewscreenFrame } from "./bridge/ViewscreenFrame";
import { PanelManager } from "./bridge/PanelManager";
import { useBridgeTheme, COALITION_THEME } from "../themes";
import { useGameSession } from "../hooks/useGameSession";
import { usePanelManager } from "../hooks/usePanelManager";

// Default panel positions — arranged around a high-mounted viewscreen
function getDefaults() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Viewscreen bounds (matches CSS: top 46px, centered, 62vw max)
  const vsW = Math.min(vw * 0.62, (vh - 130) * 16 / 9);
  const vsH = vsW * 9 / 16;
  const vsLeft = (vw - vsW) / 2;
  const vsRight = vsLeft + vsW;
  const vsTop = 46;
  const vsBottom = vsTop + vsH;

  // Panel columns — flanking the viewscreen
  const leftCol = Math.max(8, vsLeft - 212);
  const rightCol = Math.min(vw - 208, vsRight + 8);

  return {
    crew:     { x: leftCol,  y: vsTop + 4 },
    status:   { x: rightCol, y: vsTop + 4 },
    tactical: { x: leftCol,  y: vsBottom - 200 },
    comms:    { x: vsLeft,   y: vsBottom + 12 },
    log:      { x: rightCol, y: vsBottom - 240 },
    target:   { x: leftCol,  y: vsTop + 4 },
    chart:    { x: Math.floor(vw / 2 - 250), y: Math.floor(vh / 2 - 200) },
  };
}

interface BridgeProps {
  encounterData?: {
    sessionId: string;
    encounterState: any;
    opening: any;
  } | null;
  onReturnToMap?: () => void;
  autoResume?: boolean;
}

export function Bridge({ encounterData, onReturnToMap, autoResume }: BridgeProps = {}) {
  // Apply bridge theme
  useBridgeTheme(COALITION_THEME);

  // Panel visibility + layout reset
  const { visible: visiblePanels, toggle: togglePanel, resetLayout, layoutKey } = usePanelManager();

  const {
    sessionId,
    encounterState,
    narrativeLog,
    isLoading,
    isStreaming,
    alertLevel,
    orders,
    liveSpatial,
    viewscreenMode,
    viewscreenImage,
    hailState,
    executor,
    captainsLog,
    baseStardate,
    missionStartMs,
    hasSave,
    startSession,
    resumeSession,
    sendAction,
    toggleMute,
    cancelOrder,
    acceptHail,
    declineHail,
    showChart,
    setShowChart,
    lockedTargets,
    removeLockedTarget,
    missionResult,
    downloadMissionLog,
    dismissViewscreen,
  } = useGameSession(encounterData ?? undefined);

  // Open chart panel when viewscreen mode switches to "chart"
  useEffect(() => {
    if (viewscreenMode?.mode === "chart") {
      setShowChart(true);
    }
  }, [viewscreenMode, setShowChart]);

  const handleCrewClick = useCallback(
    (name: string, _role: string) => {
      sendAction(`${name}, report.`);
    },
    [sendAction]
  );

  const gameReady = !!(sessionId && encounterState);
  const [sceneReady, setSceneReady] = useState(false);
  const handleSceneReady = useCallback(() => setSceneReady(true), []);

  // Compute default panel positions when game becomes ready (fresh viewport dims)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const D = useMemo(() => getDefaults(), [gameReady]);

  // Auto-resume when navigating from galaxy map resume button
  const didAutoResume = useRef(false);
  useEffect(() => {
    if (autoResume && !gameReady && hasSave && !didAutoResume.current) {
      didAutoResume.current = true;
      resumeSession();
    }
  }, [autoResume, gameReady, hasSave, resumeSession]);

  // Ctrl+Shift+L — download mission log (debug)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "L") {
        e.preventDefault();
        downloadMissionLog();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [downloadMissionLog]);

  return (
    <div data-alert={alertLevel}>
      {/* ── Start screen (only if not preloaded from galaxy map) ── */}
      {!gameReady && !encounterData && (
        <div className="bridge-start">
          <div className="start-content">
            <h1 className="start-title">STARBRIDGE</h1>
            <p className="start-subtitle">Bridge Command RPG</p>
            <div className="start-ship">USS Meridian — Constellation-class Light Cruiser</div>
            <div className="start-actions">
              <button
                className="start-button"
                onClick={startSession}
                disabled={isLoading}
              >
                {isLoading ? "INITIALIZING..." : "BEGIN MISSION"}
              </button>
              {hasSave && (
                <button
                  className="start-button start-button--secondary"
                  onClick={resumeSession}
                  disabled={isLoading}
                >
                  RESUME MISSION
                </button>
              )}
              {onReturnToMap && (
                <button
                  className="start-button start-button--tertiary"
                  onClick={onReturnToMap}
                >
                  GALAXY MAP
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading from galaxy map */}
      {!gameReady && encounterData && (
        <div className="bridge-start">
          <div className="start-content">
            <div className="loading-text">INITIALIZING ENCOUNTER...</div>
          </div>
        </div>
      )}

      {/* ── Game UI ── */}
      {gameReady && (
        <>
          {/* Loading overlay — shown until 3D scene renders first frame */}
          {!sceneReady && (
            <div className="bridge-loading-overlay">
              <div className="bridge-loading-content">
                <div className="bridge-loading-spinner" />
                <div className="bridge-loading-text">INITIALIZING SYSTEMS</div>
              </div>
            </div>
          )}

          <BridgeLayout
            alertBar={
              <AlertStatus
                alertLevel={alertLevel}
                shipName={encounterState?.playerShip.name ?? ""}
                missionStartMs={missionStartMs}
                baseStardate={baseStardate}
                escalation={encounterState?.escalation}
              />
            }
            viewscreen={
              <ViewscreenFrame mode={viewscreenMode} alertLevel={alertLevel}>
                <ViewscreenContainer
                  alertLevel={alertLevel}
                  executor={executor}
                  onReady={handleSceneReady}
                  viewscreenMode={viewscreenMode}
                  viewscreenImage={viewscreenImage}
                  acceptHail={acceptHail}
                  declineHail={declineHail}
                  encounterState={encounterState}
                  onDismissOverlay={dismissViewscreen}
                />
                <MissionPanel objectives={encounterState?.objectives} />
              </ViewscreenFrame>
            }
            commandBar={
              <CommandInput
                onSubmit={sendAction}
                disabled={isStreaming}
                hailState={hailState}
                onToggleMute={toggleMute}
              />
            }
          >
            {/* Panels — keyed by layoutKey to force re-mount on reset */}
            <div key={layoutKey}>
              {/* Crew panel */}
              {visiblePanels.has("crew") && (
                <FloatingPanel id="crew" title="CREW" defaultPosition={D.crew}>
                  <CrewPanel
                    crew={encounterState?.crew ?? []}
                    onCrewClick={handleCrewClick}
                  />
                </FloatingPanel>
              )}

              {/* Ship status + orders */}
              {visiblePanels.has("status") && (
                <FloatingPanel id="status" title="STATUS" defaultPosition={D.status}>
                  <ShipStatus
                    ship={encounterState?.playerShip ?? { name: "", class: "", faction: "", condition: "healthy", shields: "down", weapons: "standby", systems: {} }}
                    escalation={encounterState?.escalation ?? "routine"}
                    spatialState={liveSpatial}
                  />
                  <div className="hud-divider" />
                  <OrdersPanel orders={orders} onCancel={cancelOrder} />
                </FloatingPanel>
              )}

              {/* Tactical map */}
              {visiblePanels.has("tactical") && (
                <FloatingPanel id="tactical" title="TACTICAL" defaultPosition={D.tactical} className="tactical">
                  <TacticalMap executor={executor} />
                </FloatingPanel>
              )}

              {/* Target locks — one panel per locked target */}
              {[...lockedTargets].map((tid, i) => (
                <FloatingPanel
                  key={tid}
                  id={`target-${tid}`}
                  title="TARGET LOCK"
                  defaultPosition={{ x: D.target.x, y: D.target.y + i * 180 }}
                  onClose={() => removeLockedTarget(tid)}
                >
                  <TargetLock targetId={tid} spatialState={liveSpatial} onUnlock={() => removeLockedTarget(tid)} />
                </FloatingPanel>
              ))}

              {/* Star chart — toggled by game logic, not panel manager */}
              {showChart && (
                <FloatingPanel id="chart" title="STAR CHART" defaultPosition={D.chart} className="star-chart-panel" onClose={() => setShowChart(false)}>
                  <StarChart
                    system={encounterState?.systemChart}
                    galaxy={encounterState?.galaxyChart}
                    executor={executor}
                    currentSystemId={encounterState?.systemChart?.id}
                  />
                </FloatingPanel>
              )}

              {/* Comms log — locked below viewscreen */}
              {visiblePanels.has("comms") && (
                <div className="locked-comms hud-panel fp-comms">
                  <div className="panel-titlebar">
                    <span className="panel-title">COMMS</span>
                  </div>
                  <div className="panel-body">
                    <CommPanel entries={narrativeLog} isStreaming={isStreaming} />
                  </div>
                </div>
              )}

              {/* Captain's log */}
              {visiblePanels.has("log") && (
                <FloatingPanel id="log" title="CAPTAIN'S LOG" defaultPosition={D.log} className="captains-log-panel">
                  <CaptainsLog entries={captainsLog} />
                </FloatingPanel>
              )}
            </div>

            {/* Panel manager */}
            <PanelManager visible={visiblePanels} onToggle={togglePanel} onReset={resetLayout} />

            {/* Return to galaxy map button (only when launched from map) */}
            {onReturnToMap && !missionResult && (
              <button className="return-to-map-btn" onClick={onReturnToMap}>
                RETURN TO MAP
              </button>
            )}

            {/* Mission debrief overlay */}
            {missionResult && (
              <MissionDebrief
                type={missionResult.type}
                summary={missionResult.summary}
                onReturn={onReturnToMap ?? (() => window.location.reload())}
                onDownloadLog={downloadMissionLog}
              />
            )}
          </BridgeLayout>
        </>
      )}
    </div>
  );
}
